import type { JSX } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { forbiddenOverride } from '../../../hooks/use-show-mutation-error.tsx'
import type { ToastService } from '../../../services/toast-service.tsx'
import type { ItemType } from '../../../types.ts'
import { ApiError } from '../../../utils/api-client/api-error.ts'
import type {
  CheckmarxRegion,
  Integration,
  IntegrationHealthStatusResponse,
  IntegrationInUseProblem,
  Repository,
  Tool,
} from '../../../utils/api-client/api-schemas.ts'
import {
  getIsIntegrationTypeSupportedByApi,
  IntegrationInUseProblemSchema,
  type IntegrationType,
} from '../../../utils/api-client/api-schemas.ts'
import { isForbiddenError } from '../../../utils/error-utils.ts'
import { isToolIntegration, type ToolIntegration } from '../../../utils/repository-utils.tsx'
import { parsePositiveInteger, pluralize } from '../../../utils/text-utils.ts'

export type InstanceHealth = IntegrationHealthStatusResponse

// `health` is absent until the platform has run a connection check on the instance.
export const getInstanceHealth = (integration: Integration): InstanceHealth => integration.health?.status ?? 'unknown'

// Human-readable detail about the last connection check; never contains credential material. Absent
// until a check has run, and absent on a healthy instance — only a failure carries an explanation.
export const getInstanceFailureDetail = (integration: Integration): string | undefined => integration.health?.detail

// `system` instances are owned by the admin console and render read-only; `managed` instances are
// product-managed and therefore editable.
export const getIsSystemInstance = (integration: Integration): boolean => integration.source === 'system'

// Every instance carries a required, user-facing name, so it alone identifies an instance in prose.
export const getInstanceName = (integration: Integration): string => integration.name

/**
 * The sentence explaining why Delete is closed off for this instance, or `undefined` when Delete is
 * offered. Repositories that chose this instance would lose their tool connection, so
 * `DELETE /api/v1/integrations/{id}` refuses with a 409 `IntegrationInUseProblem` while any remain.
 * Reading `repository_count` lets the drawer say so before the user commits to a confirmation.
 *
 * An absent count is not zero. A payload that omits the field — an integration embedded in another
 * resource — has not answered the question, so Delete stays offered and the 409 remains the
 * authority. The count a drawer is holding can also go stale while it is open, which is the other
 * reason this gates the affordance instead of replacing the modal's 409 handling.
 */
export const getDeleteBlockedReason = (integration: Integration): string | undefined => {
  const count = integration.repository_count
  if (count === undefined || count <= 0) {
    return undefined
  }
  return `This integration is in use by ${count} ${pluralize(count, 'repository', 'repositories')}.`
}

/**
 * The sentence explaining why a source provider's endpoint fields are closed to editing on this
 * instance, or `undefined` when they are still editable. Repositories onboarded through a connection
 * hold the old server's addresses and identifiers, so `PUT /api/v1/integrations/{id}` refuses to move
 * the connection while any remain. Reading `repository_count` lets the drawer close the fields off
 * and say why, rather than offering an edit the platform will refuse.
 *
 * Reads the count the same way `getDeleteBlockedReason` does, and for the same reasons: an absent
 * count is not zero — a payload that omits the field has not answered the question, so the fields
 * stay editable and the refusal remains the authority — and a count held by an open drawer can go
 * stale, which is why this gates the affordance instead of replacing the failure handling.
 */
export const getFixedEndpointReason = (integration: Integration): string | undefined => {
  const count = integration.repository_count
  return count === undefined || count <= 0 ? undefined : getEndpointFixedMessage(count)
}

/**
 * Why an endpoint field cannot be edited — said the same way whether the client's own count closed
 * the field off or the platform's refusal answered it, so the two cannot drift apart.
 *
 * Names neither the field nor "the server", because what is fixed differs by provider: Azure fixes
 * its organization alongside its address. Only the first anchored field carries the sentence — see
 * `getFieldErrorText` — so on Azure it lands under the server address while the organization is
 * marked without text, whichever of the two the user actually edited. The wording has to explain the
 * refusal from under either field.
 *
 * `count` is absent when a refusal's body did not parse. The sentence then drops the number and
 * keeps the explanation, which is the part the user acts on.
 */
export const getEndpointFixedMessage = (count?: number): string =>
  count === undefined
    ? 'Cannot change while repositories are onboarded through this connection. Remove them first, or create a new connection instead.'
    : `Cannot change while ${count} ${pluralize(count, 'repository is', 'repositories are')} onboarded through this connection. Remove ${pluralize(count, 'it', 'them')} first, or create a new connection instead.`

/**
 * The problem type the platform raises when a delete is refused because repositories still
 * reference the instance. Matched on rather than on the `409` itself, because `DELETE
 * /api/v1/integrations/{id}` documents its conflict as a `oneOf`: the same status also carries
 * `IntegrationModifiedConcurrentlyProblem`, which names no repositories and means something else.
 */
const INTEGRATION_IN_USE_PROBLEM_TYPE = 'pixee:problems/api/integrations/integration-in-use'

const INTEGRATION_NOT_EDITABLE_PROBLEM_TYPE = 'pixee:problems/api/integrations/integration-not-editable'

/**
 * Reports a write the server refused because the caller lacks the admin role, and says whether it did,
 * so the caller can stop there: no field is at fault, so nothing else should report it. Reachable with
 * the control hidden, since the role can be revoked while a drawer is open.
 *
 * Matched on the problem type rather than the status alone, because replace and delete also answer
 * `403` for an instance configured in the admin console, which no role can change. On these endpoints
 * that is the only other `403`, so any other `403` is the role refusal.
 */
export const reportRoleRefusal = ({
  error,
  message,
  addToast,
}: {
  error: unknown
  /** Names the permission the caller lacks, e.g. "You do not have permission to add integrations." */
  message: JSX.Element
  addToast: ToastService['addToast']
}): boolean => {
  if (!isForbiddenError(error) || error.problemType === INTEGRATION_NOT_EDITABLE_PROBLEM_TYPE) {
    return false
  }
  addToast({ ...forbiddenOverride(message), message, variant: 'error' })
  return true
}

/**
 * The refusal behind a failed delete, or `undefined` when the failure was something else and the
 * error toast is the whole of the report.
 *
 * This is the authority `getDeleteBlockedReason` defers to. That one reads a count the client
 * already holds and closes off Delete before it is pressed; this one reads the answer the server
 * gave, which is the only one that accounts for a repository bound after the count was read.
 *
 * The problem's `repositories` may be empty even though the delete was refused — the platform
 * returns that when the refusal stands but re-reading named nothing — so callers must not treat an
 * empty list as "no repositories use it".
 */
export const getIntegrationInUseProblem = (error: unknown): IntegrationInUseProblem | undefined => {
  if (!(error instanceof ApiError) || error.problemType !== INTEGRATION_IN_USE_PROBLEM_TYPE) {
    return undefined
  }
  const parsed = IntegrationInUseProblemSchema.safeParse(error.problemBody)
  return parsed.success ? parsed.data : undefined
}

// The product catalog of tools Pixee can integrate with, in the order design lists them.
// `icon` selects the brand glyph (an SCM type → SCMIcon, a Tool → ToolIcon, or none for tools
// without an asset yet). `addableType` carries the Integration `type` the "+" should open the Setup
// drawer for; an entry is addable exactly when it names a creatable type.
export type CatalogIcon = { kind: 'scm'; type: Repository['type'] } | { kind: 'tool'; tool: Tool } | { kind: 'none' }

// Every tool whose setup form, fields and docs are built. This says nothing about what may be sent:
// the spec's request union decides that, and the catalog gates on both.
const CREATABLE_INTEGRATION_TYPES = [
  'datadog',
  'arnica',
  'sonar',
  'veracode',
  'checkmarx',
  'polaris',
  'black-duck-hub',
  'checkmarx-enterprise',
  'appscan',
  // The source providers. Listed apart from the tools in the catalog, because choosing a source
  // connection is what lets repositories be added at all, which is a different act from choosing
  // where an existing repository's findings come from.
  'gitlab',
  'bitbucket',
  'azure',
  'github',
] as const satisfies readonly IntegrationType[]

export type CreatableIntegrationType = ItemType<typeof CREATABLE_INTEGRATION_TYPES>

const CREATABLE_INTEGRATION_TYPE_SET = new Set<string>(CREATABLE_INTEGRATION_TYPES)

/**
 * Whether a setup form, fields and docs exist for this type in the product. Distinct from
 * `getIsIntegrationTypeSupportedByApi`, which answers whether the *spec* has a write contract for
 * it — a type can have one without the other, which is why the catalog gates on both.
 */
export const getHasIntegrationSetupForm = (type: string): type is CreatableIntegrationType =>
  CREATABLE_INTEGRATION_TYPE_SET.has(type)

export type CatalogEntry = {
  id: string
  label: string
  icon: CatalogIcon
  isAddable: boolean
  addableType?: CreatableIntegrationType
}

/**
 * Exported so stories and tests select a real entry instead of hand-writing one. Hand-written
 * fixtures had already drifted into `isAddable: true` with no `addableType`, a combination
 * `withAddabilityGated` cannot produce.
 */
export const getCatalogEntry = (id: string): CatalogEntry | undefined => TOOL_CATALOG.find(entry => entry.id === id)

const TOOL_CATALOG: CatalogEntry[] = [
  {
    id: 'azure',
    label: 'Azure DevOps',
    icon: { kind: 'scm', type: 'azure' },
    isAddable: true,
    addableType: 'azure',
  },
  {
    id: 'appscan',
    label: 'HCL AppScan',
    icon: { kind: 'tool', tool: 'appscan' },
    isAddable: true,
    addableType: 'appscan',
  },
  {
    id: 'arnica',
    label: 'Arnica',
    icon: { kind: 'tool', tool: 'arnica_sast' },
    isAddable: true,
    addableType: 'arnica',
  },
  {
    id: 'bitbucket',
    label: 'Bitbucket',
    icon: { kind: 'scm', type: 'bitbucket' },
    isAddable: true,
    addableType: 'bitbucket',
  },
  {
    id: 'blackduck',
    label: 'Black Duck Polaris',
    icon: { kind: 'tool', tool: 'polaris_sast' },
    isAddable: true,
    addableType: 'polaris',
  },
  {
    id: 'black-duck-hub',
    label: 'Black Duck Hub',
    icon: { kind: 'tool', tool: 'black_duck_hub_sca' },
    isAddable: true,
    addableType: 'black-duck-hub',
  },
  {
    id: 'checkmarx',
    label: 'Checkmarx',
    icon: { kind: 'tool', tool: 'checkmarx' },
    isAddable: true,
    addableType: 'checkmarx',
  },
  {
    id: 'checkmarx-enterprise',
    label: 'Checkmarx Enterprise',
    icon: { kind: 'tool', tool: 'checkmarx_enterprise' },
    isAddable: true,
    addableType: 'checkmarx-enterprise',
  },
  {
    id: 'datadog',
    label: 'Datadog',
    icon: { kind: 'tool', tool: 'datadog_sast' },
    isAddable: true,
    addableType: 'datadog',
  },
  {
    id: 'github',
    label: 'GitHub',
    icon: { kind: 'scm', type: 'github' },
    isAddable: true,
    addableType: 'github',
  },
  {
    id: 'gitlab',
    label: 'GitLab',
    icon: { kind: 'scm', type: 'gitlab' },
    isAddable: true,
    addableType: 'gitlab',
  },
  { id: 'sonar', label: 'SonarQube', icon: { kind: 'tool', tool: 'sonar' }, isAddable: true, addableType: 'sonar' },
  {
    id: 'veracode',
    label: 'Veracode',
    icon: { kind: 'tool', tool: 'veracode' },
    isAddable: true,
    addableType: 'veracode',
  },
]

// Source providers (SCM) supply repositories and open PRs and are connected at install time; only the
// tools that supply findings are listed in the Available catalog. An entry whose type has no write
// contract in the spec stays listed but loses its Add action.
const withAddabilityGated = (entry: CatalogEntry): CatalogEntry =>
  entry.isAddable && entry.addableType && !getIsIntegrationTypeSupportedByApi(entry.addableType)
    ? { ...entry, isAddable: false }
    : entry

export const TOOL_INTEGRATION_CATALOG: CatalogEntry[] = TOOL_CATALOG.filter(entry => entry.icon.kind !== 'scm').map(
  withAddabilityGated
)

/**
 * The source providers, as their own catalog group.
 *
 * Separate from the tools because the two answer different questions: a tool supplies findings
 * about a repository, a source provider is how the repository reached Pixee at all. They were
 * excluded from the catalog entirely while a deployment could have only one of each, connected at
 * install time through the admin console — which is the assumption connecting several in-product
 * reverses.
 */
export const SCM_INTEGRATION_CATALOG: CatalogEntry[] = TOOL_CATALOG.filter(entry => entry.icon.kind === 'scm').map(
  withAddabilityGated
)

/**
 * The name an instance goes by once it is configured — not always the catalog's label.
 * The catalog sells the product ("HCL AppScan"); a configured instance names the tool it actually is
 * ("AppScan"). Confirmed with design rather than drift, so a future reader does not "unify" these
 * into one table.
 *
 * Black Duck used to be the clearest case and no longer is: two Black Duck products ship now, so the
 * catalog names them "Black Duck Polaris" and "Black Duck Hub" and the two tables agree for both.
 */
export const getProviderLabel = (type: Integration['type']): string =>
  match(type)
    .with('sonar', () => 'SonarQube')
    .with('checkmarx', () => 'Checkmarx')
    .with('veracode', () => 'Veracode')
    .with('appscan', () => 'AppScan')
    .with('arnica', () => 'Arnica')
    .with('datadog', () => 'Datadog')
    .with('polaris', () => 'Black Duck Polaris')
    .with('black-duck-hub', () => 'Black Duck Hub')
    .with('checkmarx-enterprise', () => 'Checkmarx Enterprise')
    .with('github', () => 'GitHub')
    .with('gitlab', () => 'GitLab')
    .with('azure', () => 'Azure DevOps')
    .with('bitbucket', () => 'Bitbucket')
    .exhaustive()

/**
 * The instances the Integrations tab is about, in the order it lists them.
 *
 * Both sources are included: a `system` instance is a connection the Enterprise Server admin console
 * still owns, and the point of showing it is that a customer can see everything the deployment is
 * connected to in one place. SCM providers are excluded — they supply repositories rather than
 * findings, are connected at install time, and are ENH-514's subject, so a grid headed "Scanner
 * Integrations" must not list GitHub. Filtering on the tool types rather than on `source` is what
 * keeps them out: today they are excluded only incidentally, by being `system`.
 *
 * Ordered by tool, then managed before system, then instance name. The collection arrives ordered by
 * the API's own concerns — managed rows paged first, config singletons sorted by id and appended — so
 * grouping a tool's instances together is the client's to do.
 */
export const getToolInstances = (integrations: Integration[]): ToolIntegration[] =>
  getUnorderedToolInstances(integrations).sort(compareInstances)

// `filter` has already allocated by the time `sort` runs, so react-query's cached array is not
// reordered under its other readers.
const getUnorderedToolInstances = (integrations: Integration[]): ToolIntegration[] =>
  integrations.filter(isToolIntegration)

const compareInstances = (left: Integration, right: Integration): number =>
  getProviderLabel(left.type).localeCompare(getProviderLabel(right.type)) ||
  Number(getIsSystemInstance(left)) - Number(getIsSystemInstance(right)) ||
  getInstanceName(left).localeCompare(getInstanceName(right))

/**
 * Every instance the Integrations tab lists, in the order it lists them.
 *
 * Source providers are included, unlike in {@link getToolInstances}: connecting one is now
 * something a user does in-product, so the tab's catalog is split into "Source Providers" and
 * "Scanner Integrations" and a GitHub connection is a card here like any other. The narrower
 * scanner-only view stays for callers that are about findings rather than about what the
 * deployment is connected to.
 */
export const getAllInstances = (integrations: Integration[]): Integration[] => [...integrations].sort(compareInstances)

/**
 * Whether this instance is one the user is being asked to do something about.
 *
 * Only a managed instance can be: the platform runs connection checks against the rows it owns, and
 * serves a config-derived system instance with no `health` at all, so reading one as unhealthy would
 * be reading a permanent "unknown" as a problem. Decided here rather than at each reader, because
 * the card's border, its failure paragraph, its health dot and the tab's banner count all turn on it
 * and a copy of the rule in any one of them is a copy that can disagree with the System badge.
 */
export const getIsNeedsAttention = (integration: Integration): boolean =>
  !getIsSystemInstance(integration) && getInstanceHealth(integration) === 'needs-attention'

// The API host each Checkmarx region resolves to. The region set itself comes from the spec's
// `CheckmarxRegion`, so this map is typed against it and a region added upstream fails to compile
// here rather than rendering an option with no host beside it. The host is shown beside the code
// because that is what a Checkmarx user sees in their own console, and because the region is the
// only field that names the host.
const CHECKMARX_REGION_API_HOSTS: Record<CheckmarxRegion, string> = {
  US: 'ast.checkmarx.net',
  US2: 'us.ast.checkmarx.net',
  EU: 'eu.ast.checkmarx.net',
  EU2: 'eu-2.ast.checkmarx.net',
  DEU: 'deu.ast.checkmarx.net',
  ANZ: 'anz.ast.checkmarx.net',
  IND: 'ind.ast.checkmarx.net',
  SNG: 'sng.ast.checkmarx.net',
  MEA: 'mea.ast.checkmarx.net',
}

// Listed rather than taken from `CheckmarxRegionSchema.options`, because the dropdown reads best
// with the two US regions first and the schema's members are alphabetical. `satisfies` still holds
// it to the spec's set.
export const CHECKMARX_REGIONS = [
  'US',
  'US2',
  'EU',
  'EU2',
  'DEU',
  'ANZ',
  'IND',
  'SNG',
  'MEA',
] as const satisfies readonly CheckmarxRegion[]

export const DEFAULT_CHECKMARX_REGION: CheckmarxRegion = 'US'

const CHECKMARX_REGION_OPTIONS = CHECKMARX_REGIONS.map(region => ({
  value: region,
  label: `${region} — ${CHECKMARX_REGION_API_HOSTS[region]}`,
}))

/**
 * Whether the field must be filled, and whether the form says so in the label.
 *
 * `optional-unmarked` is for a field whose helper text already explains that leaving it blank is a
 * choice with a consequence — Sonar's webhook secret is the only one — where the "(Optional)" marker
 * would say less than the sentence beneath it already does.
 */
export type FieldRequirement = 'required' | 'optional' | 'optional-unmarked'

/**
 * Which set of credentials a secret belongs to. The platform takes a set as a unit: supply all of it
 * to rotate, omit all of it to keep what is stored. Most tools have one set, so `primary` is the
 * default; Sonar's webhook secret and AppScan's webhook credentials rotate without the API
 * credentials being re-entered, so they form a second set.
 */
export type SecretGroup = 'primary' | 'webhook'

/**
 * Narrows a raw select value to a region, or `undefined` when it names none. The drawers hold the
 * field as a plain string, and the request schema takes only the nine the spec names.
 *
 * Update bodies need the `undefined` rather than a default: falling back there would send a region
 * change the user never made. Setup applies its own default on top, where the field is always seeded.
 * `null` is accepted because a save's changed-field bag uses it for "erase this", which is not a
 * region either.
 */
export const toCheckmarxRegionOrUndefined = (value: string | null | undefined): CheckmarxRegion | undefined =>
  CHECKMARX_REGIONS.find(region => region === value)

/**
 * Narrows a raw select value to a {@link CheckmarxRegion}, defaulting to the first region.
 *
 * Both drawers need it: setup gates Submit until a region is chosen, and manage sends the region
 * only when it changed, so in both the default is a type-level fallback rather than a value a user
 * can reach.
 */
export const toCheckmarxRegion = (value: string | undefined): CheckmarxRegion =>
  CHECKMARX_REGIONS.find(region => region === value) ?? DEFAULT_CHECKMARX_REGION

/**
 * Reads an Azure work item ID out of its text field, which is a text input over an integer.
 * Deliberately strict: {@link parsePositiveInteger} explains why it takes plain digits only.
 *
 * Both drawers report a rejection through `getFieldFormatError`, which calls this. That is
 * deliberate: the value the form accepts and the value the request carries come from one read, so
 * the check and the payload cannot drift apart the way two separate tests would.
 *
 * @param value the raw field value
 * @returns the ID, or undefined when the field is empty or not a positive integer
 */
export const parseWorkItemId = (value: string | undefined): number | undefined => parsePositiveInteger(value)

/**
 * The same read under update semantics, where a field carries a third state: `undefined` is
 * unchanged and omitted from the PUT, and `null` erases the stored ID, which is how the API says a
 * connection should stop referencing a work item. Blank is only ever reached as `null` from
 * {@link getSentFieldValue}, never as an empty string.
 *
 * Reaching this from Manage took three things, and all three are now in place: the Azure response
 * echoes `work_item_id`, so `getSentFieldValue` has a stored value to tell a cleared field from an
 * unchanged one; `getStoredValue` reports that numeric value as text instead of discarding it as a
 * non-string; and the field is no longer withheld from the Manage drawer. Undo any one and a
 * cleared field goes back to reading as unchanged, so the erase never leaves the client.
 */
export const parseWorkItemIdUpdate = (value: SentFieldValue): number | null | undefined =>
  value === null ? null : parseWorkItemId(value)

// A single connection/credential field of a creatable integration. `key` is the snake_case request
// body key. `select` fields carry `options`; `text`/`password` fields may carry a `defaultValue`
// prefilled on Setup. `password-multiline` is a masked credential whose value may contain line
// breaks, which an `<input>` of any type silently strips.
export type IntegrationFieldKind = 'text' | 'password' | 'select' | 'password-multiline'

export type IntegrationField = {
  key: string
  label: string
  kind: IntegrationFieldKind
  /** A write-only credential: rendered empty on Manage, and sent only when re-entered to rotate. */
  isSecret?: boolean
  /** Which credential set this secret rotates with. Meaningless on a non-secret field. */
  secretGroup?: SecretGroup
  /**
   * One of a tool's secrets the platform accepts only as a complete set, so Manage holds a save that
   * rotates some of them without the rest.
   */
  isRotatedAsSet?: boolean
  /** Defaults to `required`. */
  requirement?: FieldRequirement
  /** Checked client-side as an absolute URL, since the request schema rejects anything else. */
  isUrl?: boolean
  /**
   * Checked client-side as a positive integer, since the field is a text input over one and
   * `parseWorkItemId` drops whatever it cannot read. Without the check that drop is silent on an
   * update: the unreadable entry leaves the body, a merging PUT keeps the stored value, and the save
   * reports success having changed nothing.
   */
  isPositiveInteger?: boolean
  /**
   * Whether this field decides which host Pixee contacts. Changing one on the Manage drawer requires
   * every secret to be re-entered, so the platform only tests the new address with credentials the
   * user just supplied. Veracode's signing target is a URL but not one Pixee sends a request to, so
   * it is `isUrl` without being endpoint-deciding.
   */
  isEndpointDeciding?: boolean
  /**
   * Whether this field is part of the endpoint a source provider fixes once repositories have been
   * onboarded through the connection. Changing one then would move every one of those repositories
   * to a different server at once while Pixee still held the old server's addresses and identifiers
   * for them, so `RegistryIntegrationsResource` refuses the save with an `integration-endpoint-fixed`
   * problem rather than storing something quietly wrong.
   *
   * What counts as the endpoint is the provider's: for Azure DevOps it is the server address and the
   * organization together, because a repository is addressed as
   * `<base_uri>/<organization>/<project>/_git/<name>`. Guarding the address alone would let the
   * organization be edited underneath the repositories.
   *
   * Distinct from `isEndpointDeciding`, which asks whether changing the field requires re-entering
   * credentials. This one asks whether it may be changed at all.
   */
  isEndpointFixedOnceOnboarded?: boolean
  /**
   * Whether the response echoes this field whenever a value is stored, so a response without it
   * means nothing is stored. Manage then holds the save on a blank required one, as it would on a
   * value the user cleared, rather than excusing it as a field the response never reports.
   */
  isEchoedWhenStored?: boolean
  /** Rendered inside the tool's collapsed Advanced disclosure rather than in the main field list. */
  isAdvanced?: boolean
  placeholder?: string
  helperText?: string
  /**
   * Replaces `helperText` on the Manage drawer, for a field whose Setup sentence describes creating
   * an instance and would be read as a statement about an existing one. Blank is what usually
   * separates the two: on Setup it means "create it without this", while on Manage it means "keep
   * what is stored" for a field that cannot be reported, and "erase what is stored" for an optional
   * field that can. A field whose blank says something different here needs to say it here.
   */
  manageHelperText?: string
  defaultValue?: string
  options?: { value: string; label: string }[]
}

/**
 * The host field, labelled for who runs the server. "Server URL" for the tools a customer can host
 * themselves, "API base URI" for the SaaS ones, so the label tells the user whether the value is
 * theirs to choose.
 */
const hostField = ({
  label,
  defaultValue,
  helperText,
}: {
  label: 'Server URL' | 'API base URI'
  // Optional: a customer-hosted tool has no published endpoint to pre-fill, unlike the cloud
  // services whose own documented host is the right starting value.
  defaultValue?: string
  helperText: string
}): IntegrationField => ({
  key: 'base_uri',
  label,
  kind: 'text',
  isUrl: true,
  isEndpointDeciding: true,
  defaultValue,
  helperText,
})

// Mirrors `SonarQubeCloud.isEndpoint`: the host alone decides it, compared case-insensitively, which
// `URL.hostname` already lowercases for.
const SONAR_CLOUD_HOSTS = ['sonarcloud.io', 'sonarqube.us']

// `URL` reads a host out of `https:/sonarcloud.io` and `https:///sonarcloud.io`, which Java's
// `URI.getHost()` answers null for.
const HAS_AUTHORITY_PATTERN = /^[a-z][a-z\d+.-]*:\/\/[^/]/i

// `URL` decodes `sonarcloud%2eio` and folds full-width `ｓｏｎａｒｃｌｏｕｄ.io` to `sonarcloud.io`, while
// Java's `URI.getHost()` answers null for both, so the platform treats them as self-hosted.
const ESCAPED_OR_NON_ASCII_AUTHORITY_PATTERN = /^[^:]*:\/\/[^/?#]*[%\P{ASCII}]/u

/** Whether a Server URL names one of SonarQube Cloud's regions rather than a self-hosted SonarQube. */
export const getIsSonarCloudUrl = (baseUri: string | undefined): boolean => {
  const trimmed = (baseUri ?? '').trim()
  return (
    HAS_AUTHORITY_PATTERN.test(trimmed) &&
    !ESCAPED_OR_NON_ASCII_AUTHORITY_PATTERN.test(trimmed) &&
    URL.canParse(trimmed) &&
    SONAR_CLOUD_HOSTS.includes(new URL(trimmed).hostname)
  )
}

const SONAR_ORGANIZATION_FIELD: IntegrationField = {
  key: 'organization',
  label: 'Organization key',
  kind: 'text',
  isEchoedWhenStored: true,
  helperText: 'The key of the SonarQube Cloud organization that holds your projects.',
}

const SONAR_GITHUB_APP_ID_FIELD: IntegrationField = {
  key: 'github_app_id',
  label: 'GitHub App ID',
  kind: 'text',
  isPositiveInteger: true,
  isEchoedWhenStored: true,
  helperText:
    "The App ID shown on your Sonar GitHub App's settings page in GitHub. Check runs reach this instance by that ID alone, so a mismatch means Pixee ignores them.",
}

const SONAR_CREDENTIAL_FIELDS: IntegrationField[] = [
  {
    key: 'token',
    label: 'Token',
    kind: 'password',
    isSecret: true,
    helperText: 'A user token, or a project analysis token scoped to the projects Pixee should read.',
  },
  {
    key: 'webhook_secret',
    label: 'Webhook secret',
    kind: 'password',
    isSecret: true,
    secretGroup: 'webhook',
    requirement: 'optional-unmarked',
    helperText:
      "Optional. If you set one, Pixee checks it on every webhook call from your Sonar; enter the same value in Sonar's webhook settings. Left blank, Pixee accepts any call to this instance's webhook URL.",
    // A secret renders empty on Manage whether or not one is stored, so the Setup sentence — which
    // reads "left blank, Pixee accepts any call" — would tell an instance that does have a secret
    // that its webhook is unprotected. Whether one is stored is reported by
    // `webhook_secret_configured` and stated beside the webhook URL; this field only explains what
    // editing it does. `UpdateSonarIntegrationRequest.webhookSecret` takes no null to clear it, so
    // there is genuinely no way back to no secret from here, and saying so is kinder than a user
    // clearing the field and believing it worked.
    manageHelperText:
      'Leave blank to keep the secret this instance already has; enter a value to replace it. A stored secret cannot be removed here — connect a new instance without one instead.',
  },
]

/**
 * Sonar's fields for a Server URL, which decides the one identity field the platform requires.
 * SonarQube Cloud needs an organization key, and must not be sent an app id because Pixee knows each
 * region's. A self-hosted SonarQube needs the id of the GitHub App its operator registered, and
 * takes no organization key. Field order is connection → identity → credentials, so the identity
 * field sits next to the URL that decides it.
 */
export const getSonarFields = (baseUri: string | undefined): IntegrationField[] => [
  hostField({
    label: 'Server URL',
    defaultValue: 'https://sonarcloud.io',
    helperText: "SonarQube Cloud is https://sonarcloud.io. For SonarQube Server, enter your server's URL.",
  }),
  getIsSonarCloudUrl(baseUri) ? SONAR_ORGANIZATION_FIELD : SONAR_GITHUB_APP_ID_FIELD,
  ...SONAR_CREDENTIAL_FIELDS,
]

/** How an Azure DevOps connection created in the Setup drawer authenticates. */
export const AzureAuthChoiceSchema = z.enum(['pat', 'managed-identity'])
export type AzureAuthChoice = z.infer<typeof AzureAuthChoiceSchema>

const AZURE_TOKEN_FIELD: IntegrationField = {
  key: 'token',
  label: 'Personal access token',
  kind: 'password',
  isSecret: true,
  // Required here though `CreateAzureIntegrationRequest.token` is optional, and the two do not
  // disagree: the contract takes exactly one method's credentials, so each method's fields are
  // optional to the schema and required once the user has chosen that method. Deriving requirement
  // from the schema would get this wrong. The contract's third method, an Entra service principal,
  // is not offered here; see ISS-8995.
}

/** Azure DevOps's managed identity picker, the only credential field that holds no secret. */
export const AZURE_MANAGED_IDENTITY_FIELD_KEY = 'managed_identity_client_id'

/**
 * The credential fields of each Azure DevOps authentication method the Setup drawer offers. A
 * managed identity has no secret: the platform asks the VM it runs on for a token, so the user only
 * picks which of the identities the operator added to use. Its options come from the platform at
 * render time.
 */
export const AZURE_AUTH_FIELDS: Record<AzureAuthChoice, IntegrationField[]> = {
  pat: [AZURE_TOKEN_FIELD],
  'managed-identity': [
    {
      key: AZURE_MANAGED_IDENTITY_FIELD_KEY,
      label: 'Managed identity',
      kind: 'select',
      placeholder: 'Select a managed identity',
      helperText: 'One of the managed identities your administrator added, by client ID.',
    },
  ],
}

/**
 * Whether a server address is Azure DevOps Services, the only host a managed identity's token is
 * for: `https://dev.azure.com` or a legacy `*.visualstudio.com` organization, on the default port.
 * Mirrors the platform's `AzureDevOpsServices.isServicesAddress`, which refuses any other host.
 */
export const getIsAzureDevOpsServicesUrl = (baseUri: string | undefined): boolean => {
  const trimmed = (baseUri ?? '').trim()
  if (!URL.canParse(trimmed)) {
    return false
  }
  const url = new URL(trimmed)
  return (
    url.protocol === 'https:' &&
    url.port === '' &&
    (url.hostname === 'dev.azure.com' || url.hostname.endsWith('.visualstudio.com'))
  )
}

/** Azure DevOps's Setup fields for the chosen method, its credentials where the token sits on Manage. */
export const getAzureSetupFields = ({
  baseUri,
  authChoice,
}: {
  baseUri: string | undefined
  authChoice: AzureAuthChoice
}): IntegrationField[] =>
  getToolFields({ tool: 'azure', baseUri }).flatMap(field =>
    field.key === AZURE_TOKEN_FIELD.key ? AZURE_AUTH_FIELDS[authChoice] : [field]
  )

/** A tool's fields for the Server URL on screen, which only changes Sonar's. */
export const getToolFields = ({
  tool,
  baseUri,
}: {
  tool: CreatableIntegrationType
  baseUri: string | undefined
}): IntegrationField[] => (tool === 'sonar' ? getSonarFields(baseUri) : TOOL_FIELD_CONFIG[tool])

// The per-tool connection/credential fields, keyed by Integration type. The Name field is rendered
// by the drawers themselves (required on both, never a credential) and so is intentionally absent
// here. AppScan's webhook-auth fields are also absent: the drawer renders them through a method
// chooser (secret vs. basic auth) because exactly one method may be submitted.
export const TOOL_FIELD_CONFIG: Record<Exclude<CreatableIntegrationType, 'sonar'>, IntegrationField[]> = {
  datadog: [
    hostField({
      label: 'API base URI',
      defaultValue: 'https://api.datadoghq.com',
      helperText: 'EU and other regions use a different host (e.g. https://api.datadoghq.eu).',
    }),
    { key: 'api_key', label: 'API key', kind: 'password', isSecret: true },
    { key: 'application_key', label: 'Application key', kind: 'password', isSecret: true },
  ],
  arnica: [
    hostField({
      label: 'API base URI',
      defaultValue: 'https://api.app.arnica.io',
      helperText:
        "Arnica is a cloud service; leave this set to Arnica's published API endpoint unless Arnica tells you otherwise.",
    }),
    {
      key: 'api_key',
      label: 'API key',
      kind: 'password',
      isSecret: true,
      helperText: 'Generated in your Arnica organization settings.',
    },
  ],
  // Checkmarx is the one tool with no host field: it reaches an API host and an auth host, both
  // derived from the region, so a single URL would duplicate what the region already says and leave
  // two fields able to disagree.
  checkmarx: [
    {
      key: 'region',
      label: 'Region',
      kind: 'select',
      isEndpointDeciding: true,
      defaultValue: DEFAULT_CHECKMARX_REGION,
      helperText: 'The region decides the API and sign-in hosts Pixee connects to.',
      options: CHECKMARX_REGION_OPTIONS,
    },
    {
      key: 'tenant',
      label: 'Tenant account name',
      kind: 'text',
      helperText: 'Your Checkmarx One tenant name, as shown in your sign-in URL.',
    },
    {
      key: 'api_key',
      label: 'API key',
      kind: 'password',
      isSecret: true,
      helperText: 'The OAuth refresh token from Checkmarx One → API Keys.',
    },
    // No URL-override fields: this branch carried two on the guess that the Checkmarx slice would
    // ship them, and it did not. `CreateCheckmarxIntegrationRequest` landed in pixee-platform#6526
    // with `region`, `tenant` and `api_key` alone, and its region is documented as "the only thing
    // that decides where this instance is contacted". Collecting an override the request cannot
    // carry would have it stripped by `CreateIntegrationRequestSchema.parse` and silently lost.
  ],
  veracode: [
    hostField({
      label: 'API base URI',
      defaultValue: 'https://api.veracode.com',
      helperText: 'US is https://api.veracode.com; EU is https://api.veracode.eu.',
    }),
    { key: 'api_key_id', label: 'API key ID', kind: 'password', isSecret: true },
    {
      key: 'api_key_secret',
      label: 'API key secret',
      kind: 'password',
      isSecret: true,
      helperText: 'Shown once by Veracode when the credentials are generated.',
    },
    {
      key: 'signing_target',
      label: 'Signing target',
      kind: 'text',
      requirement: 'optional',
      isUrl: true,
      isAdvanced: true,
      helperText:
        'Only if Veracode instructs you to sign requests against a different host. Defaults to the API base URI.',
    },
  ],
  polaris: [
    hostField({
      label: 'API base URI',
      defaultValue: 'https://polaris.blackduck.com',
      helperText:
        'Black Duck Polaris is a cloud service; leave this set to its published API endpoint unless Black Duck tells you otherwise.',
    }),
    {
      key: 'api_token',
      label: 'Access token',
      kind: 'password',
      isSecret: true,
      helperText: 'Created under My Profile → Access Tokens in Polaris.',
    },
  ],
  'black-duck-hub': [
    // No default: unlike cloud-hosted Polaris, a Hub is the customer's own server, so there is no
    // published endpoint to pre-fill. The platform does not follow redirects, so a host that only
    // redirects to the API is rejected at save rather than at first pull.
    hostField({
      label: 'Server URL',
      helperText: 'The address that serves the Black Duck Hub API directly. Example: https://hub.example.com',
    }),
    {
      key: 'api_token',
      label: 'Access token',
      kind: 'password',
      isSecret: true,
      // The token is validated at save by listing projects, so one scoped too narrowly to do that is
      // refused even though it could read the bill of materials this integration actually pulls.
      helperText: 'Created under your profile → Access Tokens in Black Duck Hub. Must be able to list projects.',
    },
  ],
  'checkmarx-enterprise': [
    hostField({
      label: 'Server URL',
      helperText:
        'The address that serves the Checkmarx SAST API directly, without /cxrestapi. Example: https://cxsast.example.com',
    }),
    // The username and client ID are secrets though they are not sensitive: the platform stores them
    // encrypted beside the password and never echoes them, so Manage has no stored value to show.
    {
      key: 'username',
      label: 'Username',
      kind: 'text',
      isSecret: true,
      isRotatedAsSet: true,
      helperText: 'A Checkmarx SAST user that can view the projects you bind repositories to.',
    },
    { key: 'password', label: 'Password', kind: 'password', isSecret: true, isRotatedAsSet: true },
    {
      key: 'client_id',
      label: 'Client ID',
      kind: 'text',
      isSecret: true,
      isRotatedAsSet: true,
      defaultValue: 'resource_owner_sast_client',
      helperText:
        'The OIDC client Pixee signs in through, as configured in CxSAST Access Control. It must allow the password grant and the access_control_api and sast_api scopes.',
    },
    {
      key: 'client_secret',
      label: 'Client secret',
      kind: 'password',
      isSecret: true,
      isRotatedAsSet: true,
      helperText:
        "CxSAST's REST API guide publishes the default secret of resource_owner_sast_client. Enter the rotated one if an administrator changed it.",
    },
  ],
  appscan: [
    hostField({
      label: 'Server URL',
      defaultValue: 'https://cloud.appscan.com',
      helperText: "AppScan on Cloud is https://cloud.appscan.com. For AppScan Enterprise, enter your server's URL.",
    }),
    {
      key: 'api_key_id',
      label: 'API key ID',
      kind: 'password',
      isSecret: true,
      helperText: 'From Settings → API Key Management in AppScan.',
    },
    { key: 'api_key_secret', label: 'API key secret', kind: 'password', isSecret: true },
  ],
  gitlab: [
    {
      key: 'base_uri',
      label: 'GitLab server',
      kind: 'text',
      isUrl: true,
      isEndpointDeciding: true,
      isEndpointFixedOnceOnboarded: true,
      helperText: 'https://gitlab.com, or the address of your self-hosted GitLab.',
      defaultValue: 'https://gitlab.com',
    },
    {
      key: 'token',
      label: 'Access token',
      kind: 'password',
      isSecret: true,
      helperText: 'Needs the read_api scope at least, so Pixee can list projects.',
    },
    {
      key: 'webhook_secret',
      label: 'Webhook secret',
      kind: 'password',
      isSecret: true,
      secretGroup: 'webhook',
      requirement: 'optional-unmarked',
      helperText: 'Optional. Only needed if this connection receives webhook deliveries.',
    },
  ],
  // No server field: Bitbucket Cloud has one host, and a self-hosted Bitbucket is Data Center,
  // which is a different product and its own integration. Two connections differ by account.
  bitbucket: [
    { key: 'username', label: 'Bitbucket username', kind: 'text', helperText: 'Used for git operations.' },
    {
      key: 'email_address',
      label: 'Atlassian account email',
      kind: 'text',
      helperText: 'The account the API token belongs to.',
    },
    { key: 'api_token', label: 'API token', kind: 'password', isSecret: true },
  ],
  azure: [
    {
      key: 'base_uri',
      label: 'Azure DevOps server',
      kind: 'text',
      isUrl: true,
      isEndpointDeciding: true,
      isEndpointFixedOnceOnboarded: true,
      helperText: 'https://dev.azure.com, or the address of your Azure DevOps Server.',
      defaultValue: 'https://dev.azure.com',
    },
    {
      key: 'organization',
      label: 'Organization',
      kind: 'text',
      // Part of Azure's endpoint, not merely a setting on it — see `isEndpointFixedOnceOnboarded`.
      isEndpointFixedOnceOnboarded: true,
      helperText: 'The organization this connection reaches. A work item resolves only inside it.',
    },
    // The personal access token, which is what Manage edits. Setup swaps it for whichever method's
    // fields the user chose; see `getAzureSetupFields`.
    AZURE_TOKEN_FIELD,
    {
      key: 'work_item_id',
      label: 'Work item ID',
      kind: 'text',
      requirement: 'optional-unmarked',
      isPositiveInteger: true,
      helperText: "Optional. Must exist in this connection's organization.",
      manageHelperText:
        "Optional. Must exist in this connection's organization. Leave it blank to stop referencing a work item.",
    },
    {
      key: 'commit_message_prefix',
      label: 'Commit message prefix',
      kind: 'text',
      requirement: 'optional',
      helperText: 'Leave it blank to use the deployment-wide prefix.',
      manageHelperText: 'Leave it blank to fall back to the deployment-wide prefix.',
    },
    {
      key: 'webhook_password',
      label: 'Webhook password',
      kind: 'password',
      isSecret: true,
      secretGroup: 'webhook',
      requirement: 'optional-unmarked',
      helperText: 'Optional. Only needed if this connection receives webhook deliveries.',
    },
  ],
  github: [
    {
      key: 'base_uri',
      label: 'GitHub API endpoint',
      kind: 'text',
      isUrl: true,
      isEndpointDeciding: true,
      isEndpointFixedOnceOnboarded: true,
      helperText: 'https://api.github.com, or your GitHub Enterprise Server API endpoint.',
      defaultValue: 'https://api.github.com',
    },
    { key: 'app_id', label: 'App ID', kind: 'text' },
    { key: 'app_name', label: 'App name', kind: 'text' },
    {
      // Multi-line because the value is a whole PEM file. A single-line input drops the newlines as
      // the user pastes, and the platform rejects the result as unreadable (ISS-9101).
      key: 'private_key',
      label: 'Private key',
      kind: 'password-multiline',
      isSecret: true,
      helperText: 'The whole contents of the .pem file GitHub downloaded, including its BEGIN and END lines.',
    },
    {
      key: 'webhook_secret',
      label: 'Webhook secret',
      kind: 'password',
      isSecret: true,
      // Required, unlike the other providers' webhook secrets, and required by
      // `CreateGitHubIntegrationRequest` too: GitHub signs every delivery with
      // it, and a connection without one has nothing to check a delivery's signature against, on a
      // route whose path names the connection to anyone holding the URL. Being required is also why
      // it stays in the primary group rather than the `webhook` one: every GitHub connection stores
      // one, so a move always owes it.
      helperText:
        'The secret GitHub signs deliveries with. Set the same value on the App, together with this connection’s webhook URL — that URL exists only once you save, and Pixee shows it next.',
      // The Setup sentence points forward to a URL that does not exist yet, which stops being
      // true the moment there is an instance to manage: this drawer shows that URL a few fields
      // up, and the secret box below it is blank because a secret is write-only, not because
      // none is stored. What is worth saying here instead is that GitHub does not learn about a
      // replacement on its own — rotating here alone stops Pixee verifying deliveries.
      manageHelperText:
        'Leave blank to keep the secret this connection already has; enter a value to replace it. A replacement reaches GitHub only when you set the same value on the App, beside the Webhook URL shown above.',
    },
  ],
}

/** The summary text of a tool's Advanced disclosure. Absent for tools with no advanced fields. */
export const ADVANCED_SECTION_SUMMARY: Partial<Record<CreatableIntegrationType, string>> = {
  veracode: 'Advanced: request signing',
}

const WEBHOOK_TOOL_TYPES = ['sonar', 'appscan'] as const satisfies readonly CreatableIntegrationType[]

export type WebhookIntegrationType = ItemType<typeof WEBHOOK_TOOL_TYPES>

const WEBHOOK_TOOL_TYPE_SET = new Set<string>(WEBHOOK_TOOL_TYPES)

/**
 * A type predicate rather than a plain boolean, so every surface that then branches per tool — the
 * handoff steps, the unprotected-URL warning, the Manage drawer's webhook URL section — is typed
 * against this list instead of re-testing the literal `'sonar'`. Adding a third webhook tool here is
 * then a compile error in each of those `match(...).exhaustive()` calls rather than a silent
 * fall-through to AppScan's copy.
 */
export const getIsWebhookTool = (type: CreatableIntegrationType): type is WebhookIntegrationType =>
  WEBHOOK_TOOL_TYPE_SET.has(type)

const CONNECTION_WEBHOOK_ROUTE_TYPES = [
  'gitlab',
  'azure',
  'github',
] as const satisfies readonly CreatableIntegrationType[]

export type ConnectionWebhookRouteType = ItemType<typeof CONNECTION_WEBHOOK_ROUTE_TYPES>

const CONNECTION_WEBHOOK_ROUTE_TYPE_SET = new Set<string>(CONNECTION_WEBHOOK_ROUTE_TYPES)

/**
 * Takes the response's own open `type`, unlike {@link getIsWebhookTool}, because the Manage
 * drawer asks it straight off an `Integration` to decide whether to build a webhook URL at all —
 * a question that does not first pass through the setup-form catalog.
 */
export const getHasConnectionWebhookRoute = (type: string): type is ConnectionWebhookRouteType =>
  CONNECTION_WEBHOOK_ROUTE_TYPE_SET.has(type)

export const getIsFieldRequired = (field: IntegrationField): boolean => (field.requirement ?? 'required') === 'required'

/** The credential set a secret rotates with; `primary` unless the field names another. */
export const getSecretGroup = (field: IntegrationField): SecretGroup => field.secretGroup ?? 'primary'

/**
 * The value of the "Leave unchanged" option the Manage drawer adds to a select whose stored value
 * the response does not report. A `SelectBox` has no way back to "nothing chosen" once an option is
 * picked, so without this option a user who chose a region could not undo it — and choosing one is
 * what demands a full credential rotation. Never sent: `getSentFieldValue` omits it.
 */
export const UNCHANGED_SELECT_VALUE = '__unchanged__'

export const UNCHANGED_SELECT_OPTION = { value: UNCHANGED_SELECT_VALUE, label: 'Leave unchanged' }

/**
 * What a save sends for a non-secret field: a replacement value, `null` to erase the stored one, or
 * `undefined` when the save omits the field. A PUT merges, so an omitted field keeps what is stored.
 */
export type SentFieldValue = string | null | undefined

/**
 * The value a save would send for a non-secret field. This is the single predicate for "does this
 * save change this field". The request body and the endpoint-move rule below both read it, so the
 * two cannot disagree about whether a field is changing. They previously used separate tests, and
 * diverged exactly where it mattered: a field the response never reported was sent as a new value
 * while the move rule read it as unchanged, so a new host went out with no credential rotation
 * demanded — which is what that rule exists to prevent.
 *
 * Blanking an optional field the instance stores a value for sends `null`, which erases it:
 * the update requests type such a field as nullable, and `UpdateSonarIntegrationRequest` says so
 * outright for `organization` ("Send null to clear it"). Omitting it instead would keep the stored
 * value, leaving a user who typed a value by mistake with no way to remove it. A required field
 * cannot be erased, so a blank one is omitted and the drawer's own required check holds the save instead.
 */
export const getSentFieldValue = ({
  field,
  value,
  originalValue,
}: {
  field: IntegrationField
  value: string | undefined
  originalValue: string | undefined
}): SentFieldValue => {
  const trimmed = (value ?? '').trim()
  const original = (originalValue ?? '').trim()
  // The sentinel stands in for "the response reported nothing", so it is not a stored value to
  // compare against and is never sent.
  const stored = original === UNCHANGED_SELECT_VALUE ? '' : original
  if (trimmed === UNCHANGED_SELECT_VALUE || trimmed === stored) {
    return undefined
  }
  if (trimmed === '') {
    return getIsFieldRequired(field) || stored === '' ? undefined : null
  }
  return trimmed
}

const HTTP_PROTOCOLS = ['http:', 'https:']

/**
 * Whether a value names an http or https URL, which is the only kind of address Pixee ever contacts.
 *
 * `URL.canParse` on its own is not that question: it accepts any absolute URI, so
 * `mailto:ops@example.com`, `about:blank` and `javascript:alert(1)` all pass it. Neither side of the
 * contract rejects them otherwise — every one of these fields is declared `format: uri`, and the
 * platform's own check is `java.net.URI.isAbsolute()`, which is true for those schemes too — so this
 * is the one place a scheme Pixee cannot fetch is turned away.
 */
export const getIsHttpUrl = (value: string): boolean =>
  URL.canParse(value) && HTTP_PROTOCOLS.includes(new URL(value).protocol)

/**
 * Marks a reduction that is not an origin, so it can never be equal to one. Mirrors the
 * `no-authority:` tag `EffectiveOrigins.canonical` applies for the same reason: `scheme://` followed
 * by a URI's own text can spell something that reads like an origin, and the two spaces have to stay
 * disjoint for the comparison below to mean anything.
 */
const NO_ORIGIN_PREFIX = 'no-origin:'

/**
 * The origin a URL reaches, in the same reduction the platform's `EffectiveOrigins` applies:
 * `URL.origin` lowercases the scheme and host, discards userinfo, path and query, and fills in the
 * port the scheme implies.
 *
 * A value that names no origin is compared as its own lowercased text instead, so two of them stay
 * distinguishable rather than collapsing into one. That covers both the value no URL parser accepts
 * and — the case `URL.origin` alone gets wrong — the one it parses to an opaque origin, for which it
 * answers the literal string `"null"`. Reporting no move when the hosts did change is the error that
 * hands a stored credential to an address the user just typed, which is the whole failure the
 * endpoint-safety rule exists to prevent, so anything that cannot be reduced confidently is kept
 * distinct.
 */
const getOrigin = (value: string): string => {
  if (!URL.canParse(value)) {
    return NO_ORIGIN_PREFIX + value.toLowerCase()
  }
  const { origin } = new URL(value)
  return origin === 'null' ? NO_ORIGIN_PREFIX + value.toLowerCase() : origin
}

/**
 * Whether this save points an endpoint-deciding field at a different host than the stored one.
 *
 * Origins are compared rather than whole URLs because that is the comparison the platform makes: an
 * edit that only changes a path, a query, the case of the host or a redundant `:443` reaches the
 * same server, and `EffectiveOrigins` does not count it as a move. A field with no URL to reduce —
 * Checkmarx's Region, which names a host indirectly — is a move whenever its value changes at all.
 * Erasing a URL override is also a move: the instance returns to the regional host the region
 * resolves, which is a different server from the one the override named.
 */
const getHasFieldMovedOrigin = ({
  field,
  value,
  originalValue,
}: {
  field: IntegrationField
  value: string | undefined
  originalValue: string | undefined
}): boolean => {
  const sent = getSentFieldValue({ field, value, originalValue })
  if (sent === undefined) {
    return false
  }
  if (sent === null || !field.isUrl) {
    return true
  }
  return getOrigin(sent) !== getOrigin((originalValue ?? '').trim())
}

/**
 * Whether the edit in progress changes where Pixee would contact this instance. Such a save has to
 * carry every stored secret, so the platform only tests the new address with credentials the user
 * just supplied: `ManagedIntegrationWriter` refuses a write whose bundle holds a stored secret and
 * whose effective origins moved, because validating the new endpoint would send that secret to it.
 *
 * Reverting the field to its stored value makes this false again and lifts the requirement, because
 * a reverted field is one the save no longer sends.
 */
export const getHasEndpointMoved = ({
  fields,
  values,
  originalValues,
}: {
  fields: IntegrationField[]
  values: Record<string, string>
  originalValues: Record<string, string>
}): boolean =>
  fields.some(
    field =>
      field.isEndpointDeciding &&
      getHasFieldMovedOrigin({ field, value: values[field.key], originalValue: originalValues[field.key] })
  )

/**
 * The secrets a save that moves the endpoint has to carry, which is the client-side half of the
 * platform's refusal: it asks whether every secret in the bundle came from the request, and answers
 * no if any of them was recovered from the stored one.
 *
 * A tool's primary credentials are always stored, so they are always required. A webhook secret is
 * optional, and an instance that has none has nothing to protect — which is why
 * `RegistryIntegrationsResource.replacesEveryStoredSecret` asks the stored instance rather than the
 * request. Demanding one from an instance that never had one would store a secret the operator did
 * not want, and leave that instance unable to change host by any request at all.
 */
export const getSecretKeysRequiredForMove = ({
  fields,
  storedSecretGroups,
}: {
  fields: IntegrationField[]
  /** The non-primary credential sets this instance actually stores a secret for. */
  storedSecretGroups: ReadonlySet<SecretGroup>
}): string[] =>
  fields
    .filter(field => {
      if (!field.isSecret) {
        return false
      }
      const group = getSecretGroup(field)
      return group === 'primary' || storedSecretGroups.has(group)
    })
    .map(field => field.key)

// Credential-connection guidance shown in a tool's Documentation drawer: a one-paragraph summary of
// the connection model and the credentials needed, followed by ordered steps for obtaining them in
// the vendor's product, and a link to the vendor's own docs. Vendor menu paths are described loosely
// ("for example", "open …") because exact labels drift between vendor releases.
export type ToolDoc = {
  summary: string
  steps: string[]
  docsUrl?: string
  docsLabel?: string
}

export const TOOL_DOCS: Record<CreatableIntegrationType, ToolDoc> = {
  datadog: {
    summary:
      'Pixee connects to the Datadog API and pulls Code Security (SAST) findings for your repositories. You will need an API key and an Application key from your Datadog organization.',
    steps: [
      'In Datadog, open Organization Settings → API Keys and create or copy an API key.',
      'Open Organization Settings → Application Keys and create an Application key with access to Code Security.',
      'Identify your regional API host — US1 is https://api.datadoghq.com, EU is https://api.datadoghq.eu.',
      'In the Setup drawer, enter the API base URI, then paste the API key and Application key.',
    ],
    docsUrl: 'https://docs.datadoghq.com/account_management/api-app-keys/',
    docsLabel: 'Datadog API & application keys',
  },
  arnica: {
    summary:
      'Pixee connects to your Arnica tenant and pulls SAST findings. You will need your Arnica API base URI and an API key.',
    steps: [
      'In Arnica, open your organization settings and generate an API key.',
      'Copy your Arnica API base URI.',
      'In the Setup drawer, enter the API base URI and paste the API key.',
    ],
    docsUrl: 'https://docs.arnica.io/',
    docsLabel: 'Arnica documentation',
  },
  sonar: {
    summary:
      'Pixee connects to SonarQube or SonarCloud and pulls issues and security hotspots. You will need your Sonar base URI and an access token.',
    steps: [
      'In SonarQube or SonarCloud, go to My Account → Security.',
      'Generate a token — a User token, or a project analysis token scoped to the projects Pixee should read.',
      'Copy your Sonar server URL. SonarQube Cloud is https://sonarcloud.io; for SonarQube Server use your own server URL.',
      'In the Setup drawer, enter the Server URL and paste the token. For SonarQube Cloud, add your Organization key. For SonarQube Server, add the App ID of the GitHub App that posts your check runs, shown on that app’s settings page in GitHub. Set a webhook secret if you want Pixee to verify inbound calls.',
      'Copy the webhook URL Pixee shows after you connect, and add it as a webhook in Sonar — with the secret you entered, if you set one.',
    ],
    docsUrl: 'https://docs.sonarsource.com/sonarqube-server/latest/user-guide/managing-tokens/',
    docsLabel: 'SonarQube tokens',
  },
  veracode: {
    summary:
      'Pixee connects to the Veracode API with HMAC credentials and pulls findings. You will need an API key ID and an API key secret.',
    steps: [
      'In the Veracode Platform, open your account menu → API Credentials.',
      'Generate API credentials and copy the API ID and the API key — the secret is shown only once.',
      'Identify your regional API host — https://api.veracode.com (US) or https://api.veracode.eu (EU).',
      'In the Setup drawer, enter the API base URI, then paste the API key ID and API key secret.',
    ],
    docsUrl: 'https://docs.veracode.com/r/c_api_credentials',
    docsLabel: 'Veracode API credentials',
  },
  checkmarx: {
    summary:
      'Pixee connects to Checkmarx One using an OAuth refresh token and pulls scan results. You will need your region, tenant account name, and an API key.',
    steps: [
      'In Checkmarx One, open your user menu → Create API Key (or Settings → Identity and Access Management → API Keys).',
      'Generate an API key — this is an OAuth refresh token. Copy it; it is shown only once.',
      'Note your tenant (account) name and the region your tenant runs in.',
      'In the Setup drawer, choose the region, enter the tenant account name, and paste the API key.',
    ],
    docsUrl: 'https://docs.checkmarx.com/en/34965-68618-generating-an-api-key.html',
    docsLabel: 'Checkmarx One API keys',
  },
  polaris: {
    summary:
      'Pixee pulls findings from Polaris and pushes your triage decisions back to it. You will need your Polaris API base URI and an access token.',
    steps: [
      'In Polaris, open My Profile → Access Tokens.',
      'Create an access token and copy it.',
      'Copy your Polaris API base URI, for example https://polaris.blackduck.com.',
      'In the Setup drawer, enter the API base URI and paste the access token.',
    ],
    docsUrl: 'https://documentation.blackduck.com/',
    docsLabel: 'Black Duck Polaris documentation',
  },
  'black-duck-hub': {
    summary:
      'Pixee pulls the vulnerable components of a Black Duck Hub project-version and imports them as SCA findings. You will need your Hub server URL and an access token, and each repository is then bound to one project-version.',
    steps: [
      'In Black Duck Hub, open your profile → Access Tokens and create a token that can read projects.',
      'Copy your Hub server URL, for example https://hub.example.com — the address that serves the API directly, not one that redirects to it.',
      'In the Setup drawer, enter the API base URI and paste the access token.',
      "Bind a repository to a project-version in the repository's settings, using the project and version UUIDs from the project-version's URL in the Hub console.",
    ],
    docsUrl: 'https://documentation.blackduck.com/',
    docsLabel: 'Black Duck Hub documentation',
  },
  'checkmarx-enterprise': {
    summary:
      'Pixee pulls the results of the latest completed scan of a Checkmarx SAST project and imports them as findings. You will need your Checkmarx SAST server URL, the username and password of a user that can view your projects, and the OIDC client ID and secret to sign in through, and each repository is then bound to one project.',
    steps: [
      'In Checkmarx SAST, choose or create a user that can view the projects you want Pixee to read.',
      'Copy your Checkmarx SAST server URL, for example https://cxsast.example.com — without /cxrestapi, and the address that serves the API directly, not one that redirects to it.',
      "In the Setup drawer, enter the server URL and that user's username and password.",
      'Enter the OIDC client ID and secret Pixee signs in through. CxSAST provisions resource_owner_sast_client for this; use the secret from the CxSAST REST API guide unless an administrator rotated it.',
      "Bind a repository to a project in the repository's settings, using the numeric project ID from the project's URL in the Checkmarx SAST portal.",
    ],
    docsUrl: 'https://docs.checkmarx.com/',
    docsLabel: 'Checkmarx SAST documentation',
  },
  appscan: {
    summary:
      'HCL AppScan pushes scan results to Pixee through a webhook, and Pixee calls back into AppScan to create fixes. You will need AppScan API credentials plus a webhook authentication method — either a shared secret or a basic-auth username and password.',
    steps: [
      'In HCL AppScan on Cloud, open Settings → API Key Management and generate a Key ID and Secret.',
      'Copy your AppScan server URL, for example https://cloud.appscan.com.',
      'Choose a webhook authentication method: a shared secret, or a basic-auth username and password.',
      'In the Setup drawer, enter the Server URL, the API key ID and secret, and your chosen webhook authentication.',
      'Configure AppScan to send its webhook to the Pixee URL shown after setup, using the same authentication.',
    ],
    docsUrl: 'https://help.hcl-software.com/appscan/ASoC/',
    docsLabel: 'HCL AppScan on Cloud documentation',
  },
  gitlab: {
    summary:
      'A GitLab connection is one server plus one access token. Pixee uses it to list your projects, clone them, open fix merge requests and report status back. Create a second connection for each additional GitLab server you have: a token issued by one server means nothing to another.',
    steps: [
      'In GitLab, open your avatar → Edit profile → Access tokens, or a group or project’s Settings → Access tokens.',
      'Create a token with the api scope, or read_api if Pixee only needs to read.',
      'Copy your GitLab address, for example https://gitlab.com or https://gitlab.your-company.com.',
      'In the Setup drawer, enter the server address and the token.',
      'If this connection will receive webhook deliveries, enter a webhook secret and paste the URL Pixee shows into your GitLab project’s webhook settings.',
    ],
    docsUrl: 'https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html',
    docsLabel: 'GitLab access token documentation',
  },
  bitbucket: {
    summary:
      'A Bitbucket connection is one Atlassian account plus its API token. There is no server address to enter: Bitbucket Cloud has a single host, so what distinguishes two connections is the account, and therefore the workspaces that account can see. Bitbucket Data Center is a different product and is not supported here.',
    steps: [
      'In Atlassian, open Account settings → Security → API tokens and create a token.',
      'Note the email address of that Atlassian account: Bitbucket authenticates API requests with the email and token together.',
      'Note your Bitbucket username, which is separate from the email and is used for git operations.',
      'In the Setup drawer, enter the username, the account email and the API token.',
    ],
    docsUrl: 'https://support.atlassian.com/atlassian-account/docs/manage-api-tokens-for-your-atlassian-account/',
    docsLabel: 'Atlassian API token documentation',
  },
  azure: {
    summary:
      'An Azure DevOps connection is one organization plus the identity Pixee authenticates as: a personal access token, or a managed identity on the Azure VM that runs Pixee. The method is fixed once the connection is saved. The organization is part of the connection’s identity, which is also why the work item ID and commit prefix belong here rather than to the whole deployment: a work item only resolves inside its own organization.',
    steps: [
      'Note your organization name, and your server address if you run Azure DevOps Server rather than dev.azure.com.',
      'Personal access token: in Azure DevOps, open User settings → Personal access tokens and create a token with Code (read and write) and Project and team (read) scopes at least.',
      'Managed identity: your administrator first adds the identity’s client ID in the admin console under Config → Integrations. Add the identity to your Azure DevOps organization with access to the projects Pixee should see. Managed identity works only with dev.azure.com, and only when Pixee runs on an Azure VM the identity is attached to.',
      'In the Setup drawer, enter the server and the organization. If your administrator added a managed identity, choose how the connection authenticates; otherwise enter the token.',
      'If your pipelines require a work item reference on every commit, enter the work item ID and it will be added to the commits Pixee writes through this connection.',
      'If this connection will receive service hook deliveries, enter a webhook password, then create a Web Hooks service hook with the URL Pixee shows after you save.',
    ],
    docsUrl:
      'https://learn.microsoft.com/en-us/azure/devops/organizations/accounts/use-personal-access-tokens-to-authenticate',
    docsLabel: 'Azure DevOps personal access token documentation',
  },
  github: {
    summary:
      'A GitHub connection is one registered GitHub App. Every installation of an App lives on the same host and signs with the same key, so github.com and a GitHub Enterprise Server host are two connections rather than two installations of one. You create the App in GitHub first, then paste its ID and private key here, and return to the App afterwards for the webhook URL — which only exists once the connection is saved.',
    steps: [
      'In GitHub, open Settings → Developer settings → GitHub Apps and create a new App.',
      'Give it repository read and write permissions for contents, pull requests and checks.',
      'Generate a private key and download the .pem file GitHub gives you.',
      'Note the App ID and the App name from the App’s settings page.',
      'In the Setup drawer, enter the API endpoint, the App ID and name, paste the whole contents of the .pem file, and choose a webhook secret.',
      'Copy the Webhook URL Pixee shows after you save (it is also on the connection’s Manage drawer). In the App’s Webhook settings, set that URL, enter the same webhook secret and tick Active.',
      'Install the App on the organizations whose repositories Pixee should see. An App that is installed nowhere connects successfully and simply lists no repositories yet.',
    ],
    docsUrl: 'https://docs.github.com/en/apps/creating-github-apps',
    docsLabel: 'GitHub App documentation',
  },
}

// Source providers (SCM) are connected at install time and managed by an administrator, so their
// Documentation drawer explains where they are managed rather than how to add credentials here.
const SCM_DOC: ToolDoc = {
  summary:
    'Source providers supply repositories and open pull requests. They are connected when Pixee is installed and managed by your administrator — there is nothing to add here.',
  steps: [],
}

export const getCatalogEntryDoc = (entry: CatalogEntry): ToolDoc =>
  entry.addableType ? TOOL_DOCS[entry.addableType] : SCM_DOC

// Resolves the catalog entry for an integration's type, so a drawer holding a configured instance can
// open that tool's documentation.
export const getCatalogEntryForType = (type: Integration['type']): CatalogEntry | undefined =>
  TOOL_CATALOG.find(entry => entry.addableType === type)
